import React, { createContext, useContext, useState, ReactNode, SetStateAction } from 'react';
import { usePage } from '@inertiajs/react';
import ModalDialogOcorrenciasModerno from '../components/ModalDialogOcorrenciasModerno';

interface MainContextProps {
    children: ReactNode;
}

interface ContextAlunoType {
    id: number;
    nome: string;
    foto_link: string;
}

interface MainContextType {
    setShowDialogOcorrencias: React.Dispatch<SetStateAction<boolean>>;
    showDialogOcorrencias: boolean;
    criancasSelecionadas: any[];
    setCriancasSelecionadas: React.Dispatch<SetStateAction<any[]>>;
    salasSelecionadas: any[];
    setSalasSelecionadas: React.Dispatch<SetStateAction<any[]>>;
    openExtracto: boolean;
    setOpenExtracto: React.Dispatch<SetStateAction<boolean>>;
    aluno: ContextAlunoType | null;
    setAluno: React.Dispatch<SetStateAction<ContextAlunoType | null>>;
    escola: any;
    user: any;
    settings: any;
}

const MainContextE = createContext<MainContextType | undefined>(undefined);

export const MainContext: React.FC<MainContextProps> = ({ children }) => {
    const [showDialogOcorrencias, setShowDialogOcorrencias] = useState(false);
    const [criancasSelecionadas, setCriancasSelecionadas] = useState<any[]>([]);
    const [salasSelecionadas, setSalasSelecionadas] = useState<any[]>([]);
    const [openExtracto, setOpenExtracto] = useState(false);
    const [aluno, setAluno] = useState<ContextAlunoType | null>(null);

    // Dados vindos do Inertia (com fallbacks seguros)
    const { props } = usePage();
    const escola = (props as any)?.escola || {};
    const user = (props as any)?.auth?.user || {};
    const settings = (props as any)?.settings || {};

    return (
        <MainContextE.Provider
            value={{
                setShowDialogOcorrencias,
                showDialogOcorrencias,
                criancasSelecionadas,
                setCriancasSelecionadas,
                setSalasSelecionadas,
                salasSelecionadas,
                openExtracto,
                setOpenExtracto,
                aluno,
                setAluno,
                escola,
                user,
                settings,
            }}
        >
            {children}

            {/* Modais globais */}
            {showDialogOcorrencias && (
                <ModalDialogOcorrenciasModerno
                    opened={showDialogOcorrencias}
                    onClose={() => setShowDialogOcorrencias(false)}
                    criancasSelecionadas={criancasSelecionadas}
                    setCriancasSelecionadas={setCriancasSelecionadas}
                    salasSelecionadas={salasSelecionadas}
                    setSalasSelecionadas={setSalasSelecionadas}
                />
            )}
        </MainContextE.Provider>
    );
};

export const useMain = () => {
    const context = useContext(MainContextE);
    if (!context) {
        throw new Error('useMain must be used within a MainContext');
    }
    return context;
};

// Hook seguro que retorna valores padrão quando não está dentro do MainContext
// Útil para componentes que podem ser usados tanto no módulo Escola quanto no Logigate
export const useMainSafe = () => {
    const context = useContext(MainContextE);

    // Se não há contexto, retorna valores padrão
    if (!context) {
        return {
            setShowDialogOcorrencias: () => {},
            showDialogOcorrencias: false,
            criancasSelecionadas: [],
            setCriancasSelecionadas: () => {},
            salasSelecionadas: [],
            setSalasSelecionadas: () => {},
            openExtracto: false,
            setOpenExtracto: () => {},
            aluno: null,
            setAluno: () => {},
            escola: {},
            user: {},
            settings: {},
        };
    }

    return context;
};

// Hook específico para dados da escola
export const useEscola = () => {
    const { escola } = useMain();
    
    return {
        escola: escola || {},
        isAdmin: escola?.is_admin || false,
        tipo: escola?.tipo || 'escola',
        nome: escola?.nome || 'Escola',
    };
};